const SENSITIVE_KEY =
  /(email|token|authorization|cookie|password|apikey|client_?id|jwt|firstName|lastName|profile)/i;
const EMAIL_PATTERN = /[^\s@]{1,64}@[^\s@.]{1,63}(?:\.[^\s@.]{1,63}){1,16}/;
const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/;

const MAX_DEPTH = 5;
const MAX_ARRAY_LENGTH = 20;
const MAX_STRING_LENGTH = 500;

const REDACTED = "[REDACTED]";
const TRUNCATED = "[TRUNCATED]";

function redactStringPatterns(
  value: string,
  maxLength = MAX_STRING_LENGTH
): string {
  if (JWT_PATTERN.test(value)) {
    return REDACTED;
  }

  let result = value.replaceAll(new RegExp(EMAIL_PATTERN.source, "g"), REDACTED);
  result = result.replaceAll(
    /(?<![A-Za-z0-9_-])[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g,
    (match) => (JWT_PATTERN.test(match) ? REDACTED : match)
  );

  if (result.length > maxLength) {
    return `${result.slice(0, maxLength)}…`;
  }

  return result;
}

/**
 * Same email/JWT redaction as `scrub` applies to strings, with a caller-chosen
 * length cap — for stack traces and error messages where the default 500-char
 * truncation would destroy the frames Error Reporting needs for grouping.
 */
export function scrubText(value: string, maxLength: number): string {
  return redactStringPatterns(value, maxLength);
}

function scrubArray(value: unknown[], depth: number): unknown[] {
  const limit = Math.min(value.length, MAX_ARRAY_LENGTH);
  const result = value.slice(0, limit).map((item) => scrub(item, depth + 1));

  if (value.length > MAX_ARRAY_LENGTH) {
    result.push(`…${value.length - MAX_ARRAY_LENGTH} more items`);
  }

  return result;
}

export function scrub(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) {
    return TRUNCATED;
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "string") {
    return redactStringPatterns(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: scrub(value.message, depth + 1),
      stack:
        typeof value.stack === "string"
          ? redactStringPatterns(value.stack)
          : undefined,
    };
  }

  if (Array.isArray(value)) {
    return scrubArray(value, depth);
  }

  if (typeof value === "object") {
    const result: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(value)) {
      result[key] = SENSITIVE_KEY.test(key) ? REDACTED : scrub(val, depth + 1);
    }

    return result;
  }

  return value;
}
